import { Head, Link } from "@inertiajs/react"
import { ArrowLeft, CheckCircle, Clock, Edit, XCircle } from "lucide-react" // Removed AlertTriangle as it's not used

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Label } from "@/components/ui/label" // Added import for Label
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import AppLayout from "@/layouts/app-layout"
import { editProxyUrlPath, proxyUrlPath, proxyUrlsPath } from "@/routes"
import type {
  BreadcrumbItem,
  ProxyInvalidationEvent,
  ProxyPlatformState,
  ProxyUrl,
} from "@/types"

interface ProxyUrlShowProps {
  proxy_url: ProxyUrl & {
    proxy_platform_states: ProxyPlatformState[]
    proxy_invalidation_events: ProxyInvalidationEvent[]
  }
}

const formatDate = (dateString?: string | null) => {
  if (!dateString) return "N/A"
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

// getStatusDisplayName is not directly used, PlatformStatusBadge handles display logic.
// const getStatusDisplayName = (status: string) => { ... }

const PlatformStatusBadge = ({ state }: { state: ProxyPlatformState }) => {
  const isBanned = state.status === "banned"
  const inBackoff =
    state.deactivated_until && new Date(state.deactivated_until) > new Date()

  if (isBanned) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="destructive" className="cursor-help">
            <XCircle className="mr-1 h-3 w-3" /> Banned
          </Badge>
        </TooltipTrigger>
        <TooltipContent>Platform: {state.platform} - Banned</TooltipContent>
      </Tooltip>
    )
  }

  if (inBackoff) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="secondary" className="cursor-help">
            <Clock className="mr-1 h-3 w-3" /> Backoff
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          Platform: {state.platform} - Deactivated until{" "}
          {formatDate(state.deactivated_until ?? undefined)}
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* Consider making the text color dynamic or consistently green if only for "Active" */}
        <Badge
          variant="outline"
          className="cursor-help border-green-600 text-green-600"
        >
          <CheckCircle className="mr-1 h-3 w-3" /> Active
        </Badge>
      </TooltipTrigger>
      <TooltipContent>Platform: {state.platform} - Active</TooltipContent>
    </Tooltip>
  )
}

export default function ProxyUrlShow({ proxy_url }: ProxyUrlShowProps) {
  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Proxy URLs",
      href: proxyUrlsPath(),
    },
    {
      title:
        proxy_url.url.length > 50
          ? `${proxy_url.url.substring(0, 47)}...`
          : proxy_url.url, // Truncate long URLs
      href: proxyUrlPath(proxy_url.id),
    },
  ]

  return (
    <TooltipProvider>
      <AppLayout breadcrumbs={breadcrumbs}>
        <Head title={`Proxy URL: ${proxy_url.url}`} />

        <div className="flex h-full flex-1 flex-col gap-6 p-4 lg:p-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Link href={proxyUrlsPath()}>
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Back to Proxy URLs"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                </Link>
                <h1
                  className="truncate text-2xl font-semibold tracking-tight"
                  title={proxy_url.url}
                >
                  {proxy_url.url.length > 70
                    ? `${proxy_url.url.substring(0, 67)}...`
                    : proxy_url.url}
                </h1>
              </div>
              <p className="text-muted-foreground text-sm">
                Detailed information for this proxy URL.
              </p>
            </div>
            {proxy_url.id && ( // Ensure id is present before creating link
              <Link href={editProxyUrlPath(proxy_url.id)}>
                <Button>
                  <Edit className="mr-2 h-4 w-4" />
                  Edit Proxy URL
                </Button>
              </Link>
            )}
          </div>

          {/* Main Details Card */}
          <Card>
            <CardHeader>
              <CardTitle>Details</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label
                  htmlFor="proxy-url-main"
                  className="text-muted-foreground text-sm font-medium"
                >
                  URL
                </Label>
                <p id="proxy-url-main" className="font-mono text-sm break-all">
                  {proxy_url.url}
                </p>
              </div>
              <div>
                <Label
                  htmlFor="proxy-provider"
                  className="text-muted-foreground text-sm font-medium"
                >
                  Provider
                </Label>
                <p id="proxy-provider">{proxy_url.provider || "N/A"}</p>
              </div>
              <div>
                <Label
                  htmlFor="proxy-priority"
                  className="text-muted-foreground text-sm font-medium"
                >
                  Priority
                </Label>
                <p id="proxy-priority">
                  <Badge
                    variant={proxy_url.priority >= 10 ? "default" : "outline"}
                  >
                    {proxy_url.priority >= 10 ? "High" : "Default"} (
                    {proxy_url.priority})
                  </Badge>
                </p>
              </div>
              <div>
                <Label
                  htmlFor="proxy-created-at"
                  className="text-muted-foreground text-sm font-medium"
                >
                  Created At
                </Label>
                <p id="proxy-created-at">{formatDate(proxy_url.created_at)}</p>
              </div>
              <div>
                <Label
                  htmlFor="proxy-updated-at"
                  className="text-muted-foreground text-sm font-medium"
                >
                  Last Updated
                </Label>
                <p id="proxy-updated-at">{formatDate(proxy_url.updated_at)}</p>
              </div>
            </CardContent>
          </Card>

          {/* Platform States Card */}
          <Card>
            <CardHeader>
              <CardTitle>Platform States</CardTitle>
              <CardDescription>
                Status and usage of this proxy across different platforms.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {proxy_url.proxy_platform_states &&
              proxy_url.proxy_platform_states.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Platform</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Usage Count</TableHead>
                      <TableHead>Last Used</TableHead>
                      <TableHead>Deactivated Until</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {proxy_url.proxy_platform_states.map((state) => (
                      <TableRow key={state.id}>
                        <TableCell className="font-medium">
                          {state.platform}
                        </TableCell>
                        <TableCell>
                          <PlatformStatusBadge state={state} />
                        </TableCell>
                        <TableCell className="text-right">
                          {state.usage_count ?? 0}
                        </TableCell>
                        <TableCell>{formatDate(state.last_used_at)}</TableCell>
                        <TableCell>
                          {formatDate(state.deactivated_until)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-muted-foreground text-sm">
                  No platform states recorded for this proxy URL. It might be
                  new or unused.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Invalidation Events Card */}
          <Card>
            <CardHeader>
              <CardTitle>Invalidation Events</CardTitle>
              <CardDescription>
                History of invalidations for this proxy URL.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {proxy_url.proxy_invalidation_events &&
              proxy_url.proxy_invalidation_events.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Platform</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Recorded At</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {proxy_url.proxy_invalidation_events.map((event) => (
                      <TableRow key={event.id}>
                        <TableCell className="font-medium">
                          {event.platform}
                        </TableCell>
                        <TableCell>{event.reason ?? "N/A"}</TableCell>
                        <TableCell>
                          {formatDate(event.created_at ?? undefined)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-muted-foreground text-sm">
                  No invalidation events recorded for this proxy URL.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    </TooltipProvider>
  )
}
